export const DASHBOARD_LAYOUT_VERSION = 1;
export const MIN_REFRESH_INTERVAL_SECONDS = 15;
export const DEFAULT_REFRESH_INTERVAL_SECONDS = 60;

export interface GridSize {
  w: number;
  h: number;
}

/** Position and size in grid cells; x is the column (0-based), y the row (0-based). */
export interface GridRect extends GridSize {
  x: number;
  y: number;
}

export interface DashboardWidgetInstance<S = unknown> extends GridRect {
  /** Unique within a layout */
  id: string;
  type: string;
  settings?: S;
}

export interface DashboardRefreshSettings {
  enabled: boolean;
  intervalSeconds: number;
}

/** Serializable; identical whether written in TypeScript, a JSON file or returned by an API. */
export interface DashboardLayout {
  version: typeof DASHBOARD_LAYOUT_VERSION;
  /** Context keys shown in the filter bar, in order */
  filters?: string[];
  /** Initial values for context keys, overriding each key's default */
  filterDefaults?: Record<string, unknown>;
  refresh?: DashboardRefreshSettings;
  widgets: DashboardWidgetInstance[];
}

export interface DashboardGridOptions {
  columns: number;
  rowHeightPx: number;
  /** Rows shrink from `rowHeightPx` toward this so the whole layout fits the viewport, then the page scrolls */
  minRowHeightPx: number;
  gapPx: number;
  /**
   * Below this viewport width widgets stack in one column and Customize is unavailable. Outside Customize mode they
   * also stack when the grid area itself is narrower than this.
   */
  mobileBreakpointPx: number;
  /** While customizing, the grid narrows until its narrowest widget is this wide, then scrolls sideways */
  editingMinWidgetWidthPx: number;
}

export const DEFAULT_DASHBOARD_GRID_OPTIONS: DashboardGridOptions = {
  columns: 12,
  rowHeightPx: 80,
  minRowHeightPx: 64,
  gapPx: 16,
  mobileBreakpointPx: 720,
  editingMinWidgetWidthPx: 96,
};
